import { useState } from 'react';
import { api, uid, ApiError } from '../lib/api';
import { qtyLine } from '../lib/format';
import { useToast } from '../context/ToastContext';

interface TruckSuggestion { model?: string; quantities?: Record<string, number>; piles_folded?: Record<string, number>; quantities_counted?: Record<string, number>; confidence?: number; }
interface Photo { id: string; preview: string; }

const MAX_PHOTOS = 6;

export function ScanTruck() {
  const toast = useToast();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<React.ReactNode>(null);
  const [suggestion, setSuggestion] = useState<TruckSuggestion | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pm, setPm] = useState('');
  const [gm, setGm] = useState('');
  const [truck, setTruck] = useState('');
  const [msg, setMsg] = useState<{ text: string; err: boolean } | null>(null);

  async function onPhotos(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      setResult(<span className="err">Maximum {MAX_PHOTOS} photos par analyse.</span>);
      return;
    }
    const toUpload = files.slice(0, room);
    setBusy(true);
    setResult(<p className="muted">Envoi de {toUpload.length > 1 ? `${toUpload.length} photos` : 'la photo'}...</p>);
    const uploaded: Photo[] = [];
    try {
      for (const file of toUpload) {
        const id = uid();
        await api(`/v1/photos?id=${encodeURIComponent(id)}&kind=truck`, {
          method: 'POST', raw: true, headers: { 'Content-Type': file.type || 'image/jpeg' }, body: file,
        });
        uploaded.push({ id, preview: URL.createObjectURL(file) });
      }
      setPhotos((p) => [...p, ...uploaded]);
      setResult(null);
    } catch (e) { setResult(<span className="err">Envoi impossible : {e instanceof ApiError ? e.message : ''}</span>); }
    finally { setBusy(false); }
  }

  function removePhoto(id: string) {
    setPhotos((p) => p.filter((ph) => ph.id !== id));
  }

  async function analyze() {
    if (!photos.length) return;
    setResult(<p className="muted">Analyse en cours...</p>);
    try {
      const d = await api<{ piles?: Record<string, number>; piles_folded?: Record<string, number>; quantities_counted?: Record<string, number>; quantities?: Record<string, number>; note?: string; confidence?: number; model?: string }>('/v1/vision/count', { method: 'POST', body: { photo_ids: photos.map((p) => p.id) } });
      setSuggestion(d);
      setResult(
        <div className="ai-result">
          <div className="aititle">Résultat</div>
          {d.quantities_counted && Object.keys(d.quantities_counted).length > 0 && (
            <p style={{ margin: '0 0 6px' }}>Caisses comptées directement (une à une) : {qtyLine(d.quantities_counted)}</p>
          )}
          <p style={{ margin: '0 0 6px' }}>Piles dépliées détectées : {qtyLine(d.piles)}</p>
          {d.piles_folded && Object.keys(d.piles_folded).length > 0 && (
            <p style={{ margin: '0 0 6px' }}>Piles repliées détectées : {qtyLine(d.piles_folded)}</p>
          )}
          <p style={{ margin: '0 0 6px' }}>Quantités estimées : <strong>{qtyLine(d.quantities)}</strong></p>
          {d.note && <p style={{ margin: '0 0 6px' }}>{d.note}</p>}
          <p className="muted" style={{ margin: 0 }}>Confiance : {d.confidence != null ? Math.round(d.confidence * 100) + '%' : '—'}</p>
        </div>
      );
      setPm(String(d.quantities?.PM ?? 0));
      setGm(String(d.quantities?.GM ?? 0));
      setShowConfirm(true);
      toast('Analyse terminée — vérifiez avant de confirmer');
    } catch (e) { setResult(<span className="err">{e instanceof ApiError ? e.message : 'Erreur'}</span>); }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    const quantities: Record<string, number> = {};
    if (+pm > 0) quantities.PM = +pm;
    if (+gm > 0) quantities.GM = +gm;
    if (!Object.keys(quantities).length) { setMsg({ text: 'Indiquez au moins une quantité.', err: true }); return; }
    const body: Record<string, unknown> = { quantities, truck: truck.trim(), photos: photos.map((p) => p.id) };
    if (suggestion) body.ai = { model: suggestion.model, suggested: suggestion.quantities, confidence: suggestion.confidence };
    try {
      await api('/v1/receptions', { method: 'POST', body });
      setMsg({ text: 'Réception confirmée.', err: false });
      toast('Réception confirmée');
      setPhotos([]); setResult(null); setShowConfirm(false); setSuggestion(null);
    } catch (e) { setMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
  }

  return (
    <>
      <div className="pagehead"><h1>Scanner des piles de caisses</h1></div>
      <div className="card">
        <h2>Photos (piles de caisses PM/GM)</h2>
        <p className="muted">
          Camion, quai de chargement, entrepôt, coin de stockage... Prenez plusieurs photos sous plusieurs angles
          pour une vue complète (jusqu'à {MAX_PHOTOS}). L'IA suggère un comptage à partir des photos. Rien n'est
          enregistré tant que vous n'avez pas vérifié et confirmé.
        </p>
        <input type="file" accept="image/*" capture="environment" multiple onChange={onPhotos} disabled={busy || photos.length >= MAX_PHOTOS} />
        {photos.length > 0 && (
          <div className="photo-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.6rem' }}>
            {photos.map((p) => (
              <div key={p.id} style={{ position: 'relative' }}>
                <img src={p.preview} className="photo-preview" style={{ maxWidth: 120, maxHeight: 120 }} />
                <button type="button" onClick={() => removePhoto(p.id)} title="Retirer"
                        style={{ position: 'absolute', top: 2, right: 2, background: '#fff', border: '1px solid #ccc',
                                 borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', lineHeight: 1 }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="row" style={{ marginTop: '0.8rem' }}>
          <button className="ai" onClick={analyze} disabled={!photos.length || busy}>Analyser avec l'IA</button>
        </div>
        {result}
      </div>
      {showConfirm && (
        <div className="card">
          <h2>Confirmer la réception</h2>
          <form onSubmit={confirm}>
            <div className="row2">
              <div><label>Quantité PM</label><input type="number" min={0} value={pm} onChange={(e) => setPm(e.target.value)} /></div>
              <div><label>Quantité GM</label><input type="number" min={0} value={gm} onChange={(e) => setGm(e.target.value)} /></div>
            </div>
            <label>Camion</label>
            <input value={truck} onChange={(e) => setTruck(e.target.value)} placeholder="ex. camion-12" />
            <div className="row" style={{ marginTop: '0.8rem' }}><button type="submit" className="primary">Confirmer la réception</button></div>
            {msg && <div className={msg.err ? 'err' : 'ok'}>{msg.text}</div>}
          </form>
        </div>
      )}
    </>
  );
}
